import {useEffect, useMemo, useState} from 'react';
import {Box} from '@mui/material';
import {useLocation, useNavigate} from 'react-router-dom';
import ProjectProgressHeaderContent from '../components/ProjectProgressHeaderContent';
import ProjectCreationProgressTracker, {
  ProjectCreationProgressDataType,
} from '../components/ProjectCreationProgressTracker';
import BackToClientButton from '../components/BackToClientButton';
import {STEP_PROCESSING_STATUS} from '../../../common/aur-pizza-traker-progress/AurPizzaTrackerProgress';
import {useSelector} from 'react-redux';
import {AxiosError} from 'axios';
import {RootState} from '../../../../redux/store';
import {showErrorToast} from '../../../../api/utils';
import {useToast} from '../../../../contexts/toastContext/ToastContext';
import {getProjectStatusDetail} from '../../../../api/SelectiveSelfManagementService';
import AurLoadingIndicator from '../../../common/aur-loading-indicator/AurLoadingIndictor';
import {CLIENT_PERMISSION_CODES_CONSTANTS} from '../../../../constants/constants';
import {useHasClientPermission} from '../../../../hooks/permissions/usePermissionsByClientId';

interface Step {
  status: STEP_PROCESSING_STATUS;
  startDate?: string;
  completeDate?: string;
}

interface ApiItem {
  projectStatus:
    | 'PENDING'
    | 'HOSTING_WORKSPACE_PROVISIONED'
    | 'INFRASTRUCTURE_PROVISIONED'
    | 'SYNC_TO_AURORA';
  createdTime: string;
}

const buildProjectProgress = (apiResponse: {data: ApiItem[]}) => {
  const sequence = [
    {
      apiStatus: 'PENDING',
      key: 'initiateEngagement',
    },
    {
      apiStatus: 'INFRASTRUCTURE_PROVISIONED',
      key: 'infraStructureProvisioned',
    },
    {
      apiStatus: 'HOSTING_WORKSPACE_PROVISIONED',
      key: 'hostingWorkspace',
    },
    {
      apiStatus: 'SYNC_TO_AURORA',
      key: 'syncingToArora',
    },
  ] as const;

  // Default: all NotStarted
  const result: Record<string, Step> = {};

  sequence.forEach(({key}) => {
    result[key] = {
      status: 'NotStarted',
    };
  });

  if (!apiResponse?.data?.length) {
    return result;
  }

  // Lookup by status
  const statusMap = new Map(
    apiResponse.data.map((item) => [item.projectStatus, item])
  );

  for (let i = 0; i < sequence.length; i++) {
    const {apiStatus, key} = sequence[i];
    const current = statusMap.get(apiStatus);

    if (current) {
      // Current step completed
      result[key] = {
        status: 'Completed',
        startDate: current.createdTime,
        completeDate: current.createdTime,
      };

      // Next step becomes InProgress if it isn't completed
      const next = sequence[i + 1];
      if (next && !statusMap.has(next.apiStatus)) {
        result[next.key] = {
          status: 'InProgress',
          startDate: current.createdTime,
        };
        break;
      }
    }
  }

  return result;
};

const ProjectCreationProgressDetail: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {showToast} = useToast();
  const {clientId} = useSelector((state: RootState) => state.projectCreation);

  const [isLoadingReq, setIsLoadingReq] = useState(false);
  const [progressData, setProgressData] = useState<
    ProjectCreationProgressDataType | undefined
  >(undefined);

  const hasCreateProjectPermission = useHasClientPermission(
    clientId ?? '',
    CLIENT_PERMISSION_CODES_CONSTANTS.CREATE_NEW_PROJECT
  );

  const locationQueryParam = useMemo(() => {
    const queryParam = new URLSearchParams(location.search);
    return {
      projectID: queryParam.get('id'),
      projectName: queryParam.get('name'),
    };
  }, [location.search]);

  useEffect(() => {
    if (!clientId) {
      navigate('/welcome-multi-client', {replace: true});
      return;
    }

    if (!hasCreateProjectPermission) {
      navigate('/secure-page', {replace: true});
      return;
    }

    window.scrollTo(0, 0);
    /** Navigate to Client overview if page open without projectId */
    if (!locationQueryParam.projectID) {
      navigate('/welcome-single-client', {replace: true});
      return;
    }
    fetchProjectStatusDetail();
  }, []);

  const fetchProjectStatusDetail = async () => {
    try {
      setIsLoadingReq(true);
      const params = {
        clientId: clientId ?? '',
        auroraProjectId: locationQueryParam.projectID ?? '',
      };
      const response = await getProjectStatusDetail(params);
      setProgressData(buildProjectProgress(response.data));
    } catch (error: unknown) {
      const err = error as AxiosError;
      showErrorToast(showToast, err, 'right');
    } finally {
      setIsLoadingReq(false);
    }
  };

  return (
    <div className="project-creation-progress-detail-container">
      <Box className="header" display="flex" flexDirection="column">
        <ProjectProgressHeaderContent
          projectName={locationQueryParam.projectName ?? ''}
        />
      </Box>
      <Box
        className="bottom-traker-container"
        display="flex"
        flexDirection="column"
        alignItems="center"
      >
        <ProjectCreationProgressTracker
          progressType="created-project-progress"
          progressData={progressData}
        />
        <BackToClientButton />
      </Box>
      <AurLoadingIndicator isOpen={isLoadingReq} overlay />
    </div>
  );
};

export default ProjectCreationProgressDetail;
